import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import useApiRequest from '../../lib/hooks/useRequest';
import { ErrorEntity, Error as ErrorI } from '../root/context';
import { OfferCheckoutNoticeStatus } from './components/offer-checkout-notice/offer-checkout-notice';
import { CONTACT_MAIL } from './constants/offer-checkout.constants';
import { OfferCheckoutStatus } from './constants/offer-checkout-status.enum';
import OfferCheckoutPage, { OfferCheckoutPageState } from './page';
import { AcceptOfferPayload, AcceptOfferRequestDto, OfferTokenRequestDto, ValidateBankDetailsRequestDto } from './types/request';
import { BankDetailsValidationResponseDto, OfferCheckoutResponseDto, OfferFileUrlResponseDto } from './types/response';

// A dead link replaces the page at any time; the error modal leaves these out.
const PAGE_STATE_ERRORS: Partial<Record<ErrorEntity, OfferCheckoutNoticeStatus>> = {
  [ErrorEntity.OfferLinkInvalidNotFound]: OfferCheckoutStatus.LINK_INVALID,
  [ErrorEntity.OfferAlreadyAcceptedConflict]: OfferCheckoutStatus.ALREADY_ACCEPTED
};

// Before the offer is on screen there is nothing to keep, so the rate limit is a page state too.
const LOAD_ERRORS: Partial<Record<ErrorEntity, OfferCheckoutNoticeStatus>> = {
  ...PAGE_STATE_ERRORS,
  [ErrorEntity.OfferCheckoutTooManyRequests]: OfferCheckoutStatus.TOO_MANY_REQUESTS
};

// Also left out by the error modal (field errors of the admin form and the old self-checkout) – a toast keeps the filled-in form.
const TOAST_ERRORS = [
  ErrorEntity.OfferCheckoutTooManyRequests,
  ErrorEntity.OfferMailAlreadyInUseConflict,
  ErrorEntity.OfferCompanyNameAlreadyInUseConflict,
  ErrorEntity.InvalidIbanForbidden,
  ErrorEntity.InvalidBicForbidden,
  ErrorEntity.InvalidIbanAndBicForbidden
];

// The shared texts are written for admins – the customer gets a way to reach us instead.
const CUSTOMER_ERROR_TEXT: Partial<Record<ErrorEntity, string>> = {
  [ErrorEntity.OfferMailAlreadyInUseConflict]: `Für diese E-Mail-Adresse gibt es bereits ein Konto bei 7i7® flow. Bitte melde dich bei uns unter ${CONTACT_MAIL}.`,
  [ErrorEntity.OfferCompanyNameAlreadyInUseConflict]: `Dieses Unternehmen ist bei uns bereits registriert. Bitte prüfe den Firmennamen oder melde dich bei uns unter ${CONTACT_MAIL}.`
};

const OfferCheckoutWrapper = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { apiRequest } = useApiRequest();
  const [state, setState] = useState<OfferCheckoutPageState>({ status: OfferCheckoutStatus.LOADING });
  const loadedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // `apiRequest` changes once the access token of a logged-in visitor arrives – the offer is loaded only once per link.
    if (loadedTokenRef.current === token) return;
    loadedTokenRef.current = token;
    setState({ status: OfferCheckoutStatus.LOADING });

    const fetchOffer = async () => {
      const body: OfferTokenRequestDto = { token };
      const res = await apiRequest<OfferCheckoutResponseDto>('offers/checkout/view', 'POST', { body });
      // Another link was opened meanwhile – its own request decides the page.
      if (loadedTokenRef.current !== token) return;
      if (res.data) {
        setState({ status: OfferCheckoutStatus.READY, offer: res.data });
        return;
      }
      const entity = (res.error as ErrorI | undefined)?.entity;
      setState({ status: (entity && LOAD_ERRORS[entity]) ?? OfferCheckoutStatus.LOAD_FAILED });
    };
    fetchOffer();
  }, [apiRequest, token]);

  // Every other error is already shown by the error modal.
  const handleRequestError = (error: ErrorI | undefined) => {
    const pageState = error && PAGE_STATE_ERRORS[error.entity];
    if (pageState) setState({ status: pageState });
    // Without a (readable) response, e.g. offline, there is neither an error nor the error modal.
    else if (!error) toast.error('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
    else if (TOAST_ERRORS.includes(error.entity)) toast.error(CUSTOMER_ERROR_TEXT[error.entity] ?? error.message);
  };

  const validateBankDetails = async (iban: string, bic: string): Promise<BankDetailsValidationResponseDto | null> => {
    const body: ValidateBankDetailsRequestDto = { token, iban, bic };
    const res = await apiRequest<BankDetailsValidationResponseDto>('offers/checkout/iban-validation', 'POST', { body });
    if (!res.data) handleRequestError(res.error as ErrorI | undefined);
    return res.data ?? null;
  };

  // The link is short-lived – requested on click, like the PDFs in the admin.
  const handleDownloadOfferFile = async () => {
    const body: OfferTokenRequestDto = { token };
    const res = await apiRequest<OfferFileUrlResponseDto>('offers/checkout/offer-file', 'POST', { body });
    if (res.data) window.open(res.data.url, '_blank', 'noopener,noreferrer');
    else handleRequestError(res.error as ErrorI | undefined);
  };

  const handleSubmit = async (payload: AcceptOfferPayload) => {
    const body: AcceptOfferRequestDto = { token, ...payload };
    // 204 without a body – `data` is then the string 'success'.
    const res = await apiRequest('offers/checkout/accept', 'POST', { body });
    if (res.data) setState({ status: OfferCheckoutStatus.ACCEPTED });
    else handleRequestError(res.error as ErrorI | undefined);
  };

  return (
    <OfferCheckoutPage state={state} onSubmit={handleSubmit} validateBankDetails={validateBankDetails} onDownloadOfferFile={handleDownloadOfferFile} />
  );
};

export default OfferCheckoutWrapper;
